import {
  ChangeEvent,
  useState,
  useEffect,
  useCallback,
  createRef,
} from "react";
import jaroWinklerDistance from "jaro-winkler";
import { useDispatch, useSelector } from "react-redux";
import { RequiredValidatedFormFieldConfig } from "@evergiving/common/dist/es2015/form";
import {
  Belfiore,
  Parser,
  BelfiorePlace,
} from "@marketto/codice-fiscale-utils";
import defer from "lodash/defer";
import { LabelledField } from "@/lib/components/LabelledField";
import { LabelText } from "@/lib/components/LabelText";

import { RootState, VersionedStringFormState } from "@/lib/redux/types";
import { setBelfioreCode } from "@/lib/redux/actions";
import { FieldError } from "@/lib/components/FieldError";

const belfiorePlaces = Belfiore.toArray();

function BelfiorePlaceButton({
  place,
  onClick,
  selected,
  scrollIntoView,
}: {
  place: BelfiorePlace;
  onClick: (place: BelfiorePlace) => void;
  selected: boolean;
  scrollIntoView: boolean;
}) {
  const handleClick = useCallback(() => {
    onClick(place);
  }, [onClick, place]);

  const handleRef = useCallback((component: HTMLElement | null) => {
    if (component) {
      if (component.getBoundingClientRect().bottom > window.innerHeight) {
        component.scrollIntoView(false);
      }
    }
  }, []);

  return (
    // A11y seems to be taken care of elsewhere, so I'm disabling this rule here
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <li
      role="option"
      className="countryChoice"
      onClick={handleClick}
      tabIndex={-1}
      aria-selected={selected || undefined}
      ref={scrollIntoView ? handleRef : undefined}
    >
      {place.name}&nbsp;
      <span className="belfioreCode">{place.belfioreCode}</span>
    </li>
  );
}

function getBelfiorePlaceName(belfioreCode: string) {
  const belfiorePlace = belfiorePlaces.find(
    (place) => place.belfioreCode === belfioreCode
  );
  if (!belfiorePlace) {
    return "";
  }
  return belfiorePlace.name;
}

function ItalianFiscalCodeBirthPlaceField({
  name,
  config,
  open,
  setOpen,
}: {
  name: keyof VersionedStringFormState;
  config: RequiredValidatedFormFieldConfig;
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  const currentBelfioreCode = useSelector(
    (state: RootState) => state.belfioreCode.value
  );

  const anyErrors = useSelector(
    (state: RootState) => state.errors.belfioreCode
  );
  const required = !!config.requiredFieldErrorText;
  const errorId = "belfioreCode-errors";

  const [text, setText] = useState<string>(
    currentBelfioreCode === undefined
      ? ""
      : getBelfiorePlaceName(currentBelfioreCode)
  );
  const [matching, setMatching] = useState<BelfiorePlace[]>([]);

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  const [isDirty, setIsDirty] = useState(false);

  const updateMatching = useCallback(
    (newMatching: BelfiorePlace[]) => {
      const prevSelected = selectedIndex >= 0 ? matching[selectedIndex] : null;
      if (JSON.stringify(newMatching) !== JSON.stringify(matching)) {
        setMatching(newMatching);
        setSelectedIndex(prevSelected ? newMatching.indexOf(prevSelected) : -1);
      }
    },
    [matching, selectedIndex]
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target) {
        setText(event.target.value);
        setTimeout(() => {
          setOpen(true);
        }, 50);
      }
    },
    [setOpen]
  );

  useEffect(() => {
    const textNoWhitespace = text.replace(/^\s+/, "").replace(/\s+$/, "");
    if (textNoWhitespace.length === 0) {
      updateMatching([]);
    } else {
      const newMatching = belfiorePlaces
        .map((belfiorePlace) => ({
          belfiorePlace,
          distance: jaroWinklerDistance(textNoWhitespace, belfiorePlace.name, {
            caseSensitive: false,
          }),
        }))
        .filter(({ distance }) => distance >= 0.85);

      newMatching.sort((a, b) => b.distance - a.distance);
      updateMatching(
        newMatching.slice(0, 5).map(({ belfiorePlace }) => belfiorePlace)
      );
    }
  }, [text, updateMatching]);

  useEffect(() => {
    setText(
      currentBelfioreCode === undefined
        ? ""
        : getBelfiorePlaceName(currentBelfioreCode)
    );
  }, [currentBelfioreCode]);

  const inputEl = createRef<HTMLInputElement>();

  const handleFocus = useCallback(() => {
    if (inputEl.current) {
      inputEl.current.select();
    }
  }, [inputEl]);

  const dispatch = useDispatch();

  const update = useCallback(
    (belfioreCode) => {
      dispatch(setBelfioreCode(belfioreCode));
    },
    [dispatch]
  );

  const handleBlur = useCallback(() => {
    const canonicalName = text.trim().toLowerCase();
    const matchingPlaces = belfiorePlaces.filter(
      (place) => place.name.toLowerCase() === canonicalName
    );
    const belfioreCodes = Array.from(
      new Set(matchingPlaces.map((place) => place.belfioreCode))
    );
    if (belfioreCodes.length === 1) {
      if (!matchingPlaces.some((place) => place.name === text)) {
        setText(matchingPlaces[0].name);
      }
      update(belfioreCodes[0]);
    } else {
      setText("");
      update(undefined);
    }
    setOpen(false);
    setIsDirty(true);
  }, [text, update, setOpen]);

  const handleClick = useCallback(
    (belfiorePlace) => {
      setOpen(false);
      setText(belfiorePlace.name);
      update(belfiorePlace.belfioreCode);
      const { current } = inputEl;
      if (current) {
        current.focus();
        defer(() => {
          current.select();
        });
      }
    },
    [setOpen, update, inputEl]
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        if (matching.length > 0) {
          const belfiorePlace = matching[Math.max(selectedIndex, 0)];
          setOpen(false);
          setText(belfiorePlace.name);
          update(belfiorePlace.belfioreCode);
          const inputElCurrent = inputEl.current;
          if (inputElCurrent) {
            setTimeout(() => {
              inputElCurrent.select();
            }, 0);
          }
        }
      }
    },
    [matching, selectedIndex, setOpen, update, inputEl]
  );

  const moveSelection = useCallback(
    (delta: number) => {
      setSelectedIndex(
        (selectedIndex + matching.length + delta) % matching.length
      );
    },
    [selectedIndex, matching.length]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "ArrowDown" && open) {
        moveSelection(1);
      } else if (event.key === "ArrowUp" && open) {
        moveSelection(-1);
      } else if (event.key === "Escape") {
        setSelectedIndex(-1);
        setOpen(false);
        setText("");
        const inputElCurrent = inputEl.current;
        if (inputElCurrent) {
          setTimeout(() => {
            inputElCurrent.select();
          }, 0);
        }
      }
    },
    [open, moveSelection, setOpen, inputEl]
  );

  return (
    <>
      <div
        className="countryComboBox"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-owns="country-list"
        aria-controls="country-list"
        aria-required={required}
        aria-invalid={(anyErrors && isDirty) || undefined}
        aria-describedby={anyErrors ? errorId : undefined}
      >
        <div className="textField personalIdentifiationNumberField">
          <input
            id="countryInput"
            type="text"
            name={name}
            placeholder={config.placeholderText}
            value={text}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyPress={handleKeyPress}
            onKeyDown={handleKeyDown}
            ref={inputEl}
            aria-autocomplete="list"
            aria-activedescendant={
              selectedIndex >= 0
                ? `country-option-${String(matching[selectedIndex])}`
                : undefined
            }
          />
        </div>
        {open && (
          <ul role="listbox" id="country-list">
            {matching.map((belfiorePlace, index) => (
              <BelfiorePlaceButton
                key={[belfiorePlace.name, belfiorePlace.belfioreCode].join("/")}
                place={belfiorePlace}
                onClick={handleClick}
                selected={index === selectedIndex}
                scrollIntoView={index === matching.length - 1}
              />
            ))}
          </ul>
        )}
      </div>
      <div role="alert">
        {anyErrors && isDirty ? (
          <FieldError
            id={errorId}
            messages={
              config.requiredFieldErrorText && !text
                ? [config.requiredFieldErrorText]
                : []
            }
          />
        ) : null}
      </div>
    </>
  );
}

function ItalianFiscalCodeDisplay() {
  const firstName = useSelector((state: RootState) => state.firstName.value);
  const lastName = useSelector((state: RootState) => state.lastName.value);
  const gender = useSelector((state: RootState) => state.gender.value);
  const dateOfBirth = useSelector(
    (state: RootState) => state.dateOfBirth.value
  );

  if (
    firstName !== undefined &&
    lastName !== undefined &&
    gender !== undefined &&
    dateOfBirth !== undefined
  ) {
    const [yearStr, monthStr, dayStr] = dateOfBirth.split("-").map(Number);

    const year = Number(yearStr);
    const month = Number(monthStr) - 1;
    const day = Number(dayStr);

    if (
      (day === 1 ||
        day === 2 ||
        day === 3 ||
        day === 4 ||
        day === 5 ||
        day === 6 ||
        day === 7 ||
        day === 8 ||
        day === 9 ||
        day === 10 ||
        day === 11 ||
        day === 12 ||
        day === 13 ||
        day === 14 ||
        day === 15 ||
        day === 16 ||
        day === 17 ||
        day === 18 ||
        day === 19 ||
        day === 20 ||
        day === 21 ||
        day === 22 ||
        day === 23 ||
        day === 24 ||
        day === 25 ||
        day === 26 ||
        day === 27 ||
        day === 28 ||
        day === 29 ||
        day === 30 ||
        day === 31) &&
      (month === 0 ||
        month === 1 ||
        month === 2 ||
        month === 3 ||
        month === 4 ||
        month === 5 ||
        month === 6 ||
        month === 7 ||
        month === 8 ||
        month === 9 ||
        month === 10 ||
        month === 11)
    ) {
      const codiceFiscale = Parser.encodeCf({
        lastName,
        firstName,
        year,
        month,
        day,
        gender: gender === "female" ? "F" : "M",
        place: "Catania",
      });

      return <div>{codiceFiscale}</div>;
    }
  }

  return null;
}

export function ItalianFiscalCodeField({
  name,
  config,
}: {
  name: keyof VersionedStringFormState;
  config: RequiredValidatedFormFieldConfig;
}) {
  const required = !!config.requiredFieldErrorText;
  const [open, setOpen] = useState<boolean>(false);

  return (
    <LabelledField id={name}>
      <LabelText requiredIndicator={required}>{config.labelText}</LabelText>

      <ItalianFiscalCodeBirthPlaceField
        name={name}
        config={config}
        open={open}
        setOpen={setOpen}
      />
      {!open && <ItalianFiscalCodeDisplay />}
    </LabelledField>
  );
}

// Test with: Biegno
